/**
 * AdminPromoCodesTab Component
 * Promo code management interface for administrators
 */
import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useState, useEffect, useRef } from 'preact/hooks';
import { formatDateTime } from '../../utils/dateUtils.js';
import {
  getPromoCodes,
  createPromoCode,
  updatePromoCode,
  deletePromoCode,
  getPromoCodeRedemptions,
  getPromoCodeHistory,
  resetPromoCodeRedemption
} from '../../services/adminService.js';

const PAGE_SIZE = 25;

// Generate random 6-character uppercase promo code
const generatePromoCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// Action labels and colors for history
const ACTION_LABELS = {
  created: { label: 'Created', color: '#22c55e' },
  updated: { label: 'Updated', color: '#3b82f6' },
  deactivated: { label: 'Deactivated', color: '#ef4444' },
  reactivated: { label: 'Reactivated', color: '#22c55e' },
  redeemed: { label: 'Redeemed', color: '#a855f7' },
  reserved: { label: 'Reserved', color: '#f59e0b' },
  revoked: { label: 'Revoked', color: '#ef4444' },
  redemption_reset: { label: 'Redemption Reset', color: '#3b82f6' }
};

// Status labels and colors for redemptions
const REDEMPTION_STATUS = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  confirmed: { label: 'Confirmed', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  revoked: { label: 'Revoked', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  reset: { label: 'Reset', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' }
};

export function AdminPromoCodesTab() {
  const [promoCodes, setPromoCodes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [resettingId, setResettingId] = useState(null);
  const [confirmResetId, setConfirmResetId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    promo_type: 'percentage',
    discount_percent: '',
    extra_months: '',
    expires_at: '',
    is_active: true,
    new_customers_only: false,
    max_uses: '',
    demo_required: false,
    demo_max_days_ago: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const searchTimeout = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(0);
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  // Load promo codes when filters change
  useEffect(() => {
    loadPromoCodes();
  }, [currentPage, debouncedSearch, showActiveOnly]);

  const loadPromoCodes = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getPromoCodes({
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        search: debouncedSearch,
        activeOnly: showActiveOnly
      });

      setPromoCodes(data.items || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('[AdminPromoCodes] Load error:', err);
      setError(err?.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      promo_type: 'percentage',
      discount_percent: '',
      extra_months: '',
      expires_at: '',
      is_active: true,
      new_customers_only: false,
      max_uses: '',
      demo_required: false,
      demo_max_days_ago: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        code: formData.code.toUpperCase().trim(),
        promo_type: formData.promo_type,
        is_active: formData.is_active,
        new_customers_only: formData.new_customers_only,
        demo_required: formData.demo_required
      };

      // Type-specific fields
      if (formData.promo_type === 'percentage') {
        payload.discount_percent = parseFloat(formData.discount_percent);
      } else {
        payload.extra_months = parseInt(formData.extra_months);
      }

      if (formData.description.trim()) {
        payload.description = formData.description.trim();
      }
      if (formData.expires_at) {
        payload.expires_at = new Date(formData.expires_at).toISOString();
      }
      if (formData.max_uses && formData.max_uses.trim()) {
        payload.max_uses = parseInt(formData.max_uses);
      }
      if (formData.demo_required && formData.demo_max_days_ago) {
        payload.demo_max_days_ago = parseInt(formData.demo_max_days_ago);
      }

      await createPromoCode(payload);
      setSuccess('Promo code created successfully');
      setShowCreateModal(false);
      resetForm();
      await loadPromoCodes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminPromoCodes] Create error:', err);
      setError(err?.message || 'Failed to create promo code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedPromo) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {};

      if (formData.description !== (selectedPromo.description || '')) {
        payload.description = formData.description.trim() || null;
      }
      if (formData.is_active !== selectedPromo.is_active) {
        payload.is_active = formData.is_active;
      }
      if (formData.new_customers_only !== selectedPromo.new_customers_only) {
        payload.new_customers_only = formData.new_customers_only;
      }
      if (formData.demo_required !== (selectedPromo.demo_required || false)) {
        payload.demo_required = formData.demo_required;
      }

      // Handle type-specific fields
      const promoType = selectedPromo.promo_type || 'percentage';
      if (promoType === 'percentage') {
        const newPercent = parseFloat(formData.discount_percent);
        if (newPercent !== selectedPromo.discount_percent) {
          payload.discount_percent = newPercent;
        }
      } else {
        const newExtraMonths = parseInt(formData.extra_months);
        if (newExtraMonths !== selectedPromo.extra_months) {
          payload.extra_months = newExtraMonths;
        }
      }

      // Handle max_uses
      const newMaxUses = formData.max_uses ? parseInt(formData.max_uses) : null;
      if (newMaxUses !== selectedPromo.max_uses) {
        payload.max_uses = newMaxUses;
      }

      // Handle demo_max_days_ago
      const newDemoMaxDays = formData.demo_max_days_ago ? parseInt(formData.demo_max_days_ago) : null;
      if (newDemoMaxDays !== (selectedPromo.demo_max_days_ago || null)) {
        payload.demo_max_days_ago = newDemoMaxDays;
      }

      // Handle expires_at
      const newExpires = formData.expires_at ? new Date(formData.expires_at).toISOString() : null;
      const oldExpires = selectedPromo.expires_at ? new Date(selectedPromo.expires_at).toISOString() : null;
      if (newExpires !== oldExpires) {
        payload.expires_at = newExpires;
      }

      if (Object.keys(payload).length === 0) {
        setShowEditModal(false);
        return;
      }

      await updatePromoCode(selectedPromo.id, payload);
      setSuccess('Promo code updated successfully');
      setShowEditModal(false);
      setSelectedPromo(null);
      await loadPromoCodes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminPromoCodes] Update error:', err);
      setError(err?.message || 'Failed to update promo code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (promo) => {
    if (!confirm(`Are you sure you want to deactivate promo code "${promo.code}"?`)) return;

    try {
      await deletePromoCode(promo.id);
      setSuccess(`Promo code "${promo.code}" deactivated`);
      await loadPromoCodes();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('[AdminPromoCodes] Delete error:', err);
      setError(err?.message || 'Failed to deactivate promo code');
    }
  };

  const openEditModal = (promo) => {
    setSelectedPromo(promo);
    setFormData({
      code: promo.code,
      description: promo.description || '',
      promo_type: promo.promo_type || 'percentage',
      discount_percent: promo.discount_percent ? promo.discount_percent.toString() : '',
      extra_months: promo.extra_months ? promo.extra_months.toString() : '',
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '',
      is_active: promo.is_active,
      new_customers_only: promo.new_customers_only,
      max_uses: promo.max_uses ? promo.max_uses.toString() : '',
      demo_required: promo.demo_required || false,
      demo_max_days_ago: promo.demo_max_days_ago ? promo.demo_max_days_ago.toString() : ''
    });
    setShowEditModal(true);
  };

  const openRedemptionsModal = async (promo) => {
    setSelectedPromo(promo);
    setShowRedemptionsModal(true);
    setRedemptionsLoading(true);
    setConfirmResetId(null);

    try {
      const data = await getPromoCodeRedemptions(promo.id, { limit: 50 });
      setRedemptions(data.items || []);
    } catch (err) {
      console.error('[AdminPromoCodes] Redemptions error:', err);
      setError(err?.message || 'Failed to load redemptions');
    } finally {
      setRedemptionsLoading(false);
    }
  };

  const handleResetRedemption = async (redemptionId) => {
    if (!selectedPromo) return;
    setResettingId(redemptionId);
    try {
      await resetPromoCodeRedemption(selectedPromo.id, redemptionId);
      // Refresh redemptions list
      const data = await getPromoCodeRedemptions(selectedPromo.id, { limit: 50 });
      setRedemptions(data.items || []);
      setConfirmResetId(null);
      // Refresh main list to update redemption_count
      await loadPromoCodes();
    } catch (err) {
      console.error('[AdminPromoCodes] Reset error:', err);
      setError(err?.message || 'Failed to reset redemption');
    } finally {
      setResettingId(null);
    }
  };

  const openHistoryModal = async (promo) => {
    setSelectedPromo(promo);
    setShowHistoryModal(true);
    setHistoryLoading(true);

    try {
      const data = await getPromoCodeHistory(promo.id, { limit: 100 });
      setHistory(data.items || []);
    } catch (err) {
      console.error('[AdminPromoCodes] History error:', err);
      setError(err?.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatHistoryDetails = (detailsJson) => {
    if (!detailsJson) return null;
    try {
      const details = JSON.parse(detailsJson);
      if (details.changes) {
        // Update action - show changes
        return Object.entries(details.changes).map(([field, change]) => (
          <div key={field} className="admin-history-change">
            <strong>{field}:</strong> {String(change.from)} &rarr; {String(change.to)}
          </div>
        ));
      }
      if (details.discount_amount !== undefined) {
        // Redemption action
        return (
          <div className="admin-history-change">
            Discount: &euro;{details.discount_amount?.toFixed(2)} ({details.discount_percent}%)
          </div>
        );
      }
      // Creation or other actions - show key details
      return Object.entries(details).map(([key, value]) => (
        <div key={key} className="admin-history-change">
          <strong>{key}:</strong> {String(value)}
        </div>
      ));
    } catch {
      return detailsJson;
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="admin-promo-codes-tab">
      {/* Section Header */}
      <div className="admin-section-header">
        <div className="admin-section-title">
          <h2>Promo Code Management</h2>
          <span className="admin-section-count">{totalCount} total</span>
        </div>
      </div>

      {/* Header with search and create button */}
      <div className="admin-tab-header">
        <div className="admin-search-row">
          <div className="admin-search-container">
            <input
              type="text"
              placeholder="Search promo codes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search-input"
            />
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
          </div>
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
            />
            Active only
          </label>
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="admin-btn admin-btn--primary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            Create Promo Code
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="admin-alert admin-alert--error">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M15 9l-6 6M9 9l6 6"/>
          </svg>
          {error}
          <button onClick={() => setError('')} className="admin-alert-close">&times;</button>
        </div>
      )}
      {success && (
        <div className="admin-alert admin-alert--success">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
          {success}
        </div>
      )}

      {/* Table */}
      <div className="admin-table-container">
        {loading ? (
          <div className="admin-loading">
            <div className="admin-spinner"></div>
            Loading promo codes...
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="admin-empty-state admin-empty-state--glass">
            <div className="admin-empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"/>
                <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"/>
                <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"/>
              </svg>
            </div>
            <h3>No promo codes found</h3>
            <p>Create your first promo code to offer discounts to customers.</p>
            <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="admin-btn admin-btn--primary" style={{ marginTop: '16px' }}>
              Create Your First Promo Code
            </button>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Usage Limits</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.map((promo) => (
                <tr key={promo.id} className={!promo.is_active ? 'admin-row--inactive' : ''}>
                  <td>
                    <code className="admin-promo-code">{promo.code}</code>
                    {promo.new_customers_only && (
                      <span className="admin-badge admin-badge--info" title="New customers only">New</span>
                    )}
                    {promo.demo_required && (
                      <span className="admin-badge admin-badge--warning" title="Requires demo access">Demo</span>
                    )}
                  </td>
                  <td>
                    {(promo.promo_type || 'percentage') === 'percentage' ? (
                      <strong>{promo.discount_percent}%</strong>
                    ) : (
                      <strong style={{ color: '#22c55e' }}>+{promo.extra_months} months</strong>
                    )}
                  </td>
                  <td className="admin-cell--limits">
                    <span title="Total uses allowed">
                      {promo.max_uses ? `${promo.redemption_count}/${promo.max_uses}` : 'Unlimited'}
                    </span>
                  </td>
                  <td>
                    {promo.expires_at ? (
                      <span className={new Date(promo.expires_at) < new Date() ? 'admin-text--danger' : ''}>
                        {formatDateTime(promo.expires_at, { dateOnly: true })}
                      </span>
                    ) : (
                      <span className="admin-text--muted">Never</span>
                    )}
                  </td>
                  <td>
                    {promo.is_active ? (
                      <span className="admin-badge admin-badge--success">Active</span>
                    ) : (
                      <span className="admin-badge admin-badge--inactive">Inactive</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => openRedemptionsModal(promo)}
                      className="admin-link"
                      disabled={promo.redemption_count === 0}
                    >
                      {promo.redemption_count}
                    </button>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button
                        onClick={() => openHistoryModal(promo)}
                        className="admin-btn admin-btn--small admin-btn--ghost"
                        title="View History"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </button>
                      <button
                        onClick={() => openEditModal(promo)}
                        className="admin-btn admin-btn--small admin-btn--secondary"
                        title="Edit"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      {promo.is_active && (
                        <button
                          onClick={() => handleDelete(promo)}
                          className="admin-btn admin-btn--small admin-btn--danger"
                          title="Deactivate"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <button
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="admin-btn admin-btn--small"
          >
            Previous
          </button>
          <span className="admin-pagination-info">
            Page {currentPage + 1} of {totalPages} ({totalCount} total)
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
            disabled={currentPage >= totalPages - 1}
            className="admin-btn admin-btn--small"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && createPortal(
        <div className="admin-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="admin-modal admin-modal--glass" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create Promo Code</h3>
              <button onClick={() => setShowCreateModal(false)} className="admin-modal-close">&times;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label>Code *</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., LAUNCH20"
                      required
                      maxLength={50}
                      className="admin-input"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, code: generatePromoCode() })}
                      className="admin-btn admin-btn--secondary"
                      title="Generate random 6-letter code"
                    >
                      Generate
                    </button>
                  </div>
                  <small>Alphanumeric characters, dashes, and underscores only</small>
                </div>
                <div className="admin-form-group">
                  <label>Promo Type *</label>
                  <select
                    value={formData.promo_type}
                    onChange={(e) => setFormData({ ...formData, promo_type: e.target.value })}
                    className="admin-input"
                  >
                    <option value="percentage">Percentage Discount</option>
                    <option value="extra_months">Extra Months</option>
                  </select>
                </div>
                {formData.promo_type === 'percentage' ? (
                  <div className="admin-form-group">
                    <label>Discount Percentage *</label>
                    <input
                      type="number"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                      placeholder="e.g., 20"
                      required
                      min="0"
                      max="100"
                      step="0.1"
                      className="admin-input"
                    />
                  </div>
                ) : (
                  <div className="admin-form-group">
                    <label>Extra Months *</label>
                    <input
                      type="number"
                      value={formData.extra_months}
                      onChange={(e) => setFormData({ ...formData, extra_months: e.target.value })}
                      placeholder="e.g., 2"
                      required
                      min="1"
                      max="12"
                      className="admin-input"
                    />
                    <small>Number of bonus months added to the subscription (1-12)</small>
                  </div>
                )}
                <div className="admin-form-group">
                  <label>Max Total Uses</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Unlimited"
                    min="1"
                    className="admin-input"
                  />
                  <small>Leave empty for unlimited</small>
                </div>
                <div className="admin-form-group">
                  <label>Description (internal)</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Launch promotion for Q1"
                    maxLength={255}
                    className="admin-input"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Expires At</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="admin-input"
                  />
                  <small>Leave empty for no expiration (UTC timezone)</small>
                </div>
                <div className="admin-form-group admin-form-group--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.new_customers_only}
                      onChange={(e) => setFormData({ ...formData, new_customers_only: e.target.checked })}
                    />
                    New customers only
                  </label>
                  <small>Only users without a prior PRO subscription can use this code</small>
                </div>
                <div className="admin-form-group admin-form-group--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.demo_required}
                      onChange={(e) => setFormData({ ...formData, demo_required: e.target.checked })}
                    />
                    Require demo access
                  </label>
                  <small>Only users who have had demo access can use this code</small>
                </div>
                {formData.demo_required && (
                  <div className="admin-form-group">
                    <label>Demo Max Days Ago</label>
                    <input
                      type="number"
                      value={formData.demo_max_days_ago}
                      onChange={(e) => setFormData({ ...formData, demo_max_days_ago: e.target.value })}
                      placeholder="e.g., 30"
                      min="1"
                      className="admin-input"
                    />
                    <small>Demo must have ended within this many days. Leave empty for any demo.</small>
                  </div>
                )}
              </div>
              <div className="admin-modal-footer">
                <button type="button" onClick={() => setShowCreateModal(false)} className="admin-btn admin-btn--secondary">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="admin-btn admin-btn--primary">
                  {submitting ? 'Creating...' : 'Create Promo Code'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPromo && createPortal(
        <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="admin-modal admin-modal--glass" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Promo Code: {selectedPromo.code}</h3>
              <button onClick={() => setShowEditModal(false)} className="admin-modal-close">&times;</button>
            </div>
            <form onSubmit={handleEdit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label>Code</label>
                  <input type="text" value={formData.code} disabled className="admin-input admin-input--disabled" />
                  <small>Code cannot be changed</small>
                </div>
                <div className="admin-form-group">
                  <label>Promo Type</label>
                  <input
                    type="text"
                    value={formData.promo_type === 'percentage' ? 'Percentage Discount' : 'Extra Months'}
                    disabled
                    className="admin-input admin-input--disabled"
                  />
                  <small>Type cannot be changed after creation</small>
                </div>
                {formData.promo_type === 'percentage' ? (
                  <div className="admin-form-group">
                    <label>Discount Percentage *</label>
                    <input
                      type="number"
                      value={formData.discount_percent}
                      onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                      required
                      min="0"
                      max="100"
                      step="0.1"
                      className="admin-input"
                    />
                  </div>
                ) : (
                  <div className="admin-form-group">
                    <label>Extra Months *</label>
                    <input
                      type="number"
                      value={formData.extra_months}
                      onChange={(e) => setFormData({ ...formData, extra_months: e.target.value })}
                      required
                      min="1"
                      max="12"
                      className="admin-input"
                    />
                    <small>Number of bonus months added to the subscription (1-12)</small>
                  </div>
                )}
                <div className="admin-form-group">
                  <label>Max Total Uses</label>
                  <input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                    placeholder="Unlimited"
                    min="1"
                    className="admin-input"
                  />
                  <small>Leave empty for unlimited</small>
                </div>
                <div className="admin-form-group">
                  <label>Description (internal)</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    maxLength={255}
                    className="admin-input"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Expires At</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="admin-input"
                  />
                </div>
                <div className="admin-form-group admin-form-group--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="admin-form-group admin-form-group--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.new_customers_only}
                      onChange={(e) => setFormData({ ...formData, new_customers_only: e.target.checked })}
                    />
                    New customers only
                  </label>
                </div>
                <div className="admin-form-group admin-form-group--checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.demo_required}
                      onChange={(e) => setFormData({ ...formData, demo_required: e.target.checked })}
                    />
                    Require demo access
                  </label>
                </div>
                {formData.demo_required && (
                  <div className="admin-form-group">
                    <label>Demo Max Days Ago</label>
                    <input
                      type="number"
                      value={formData.demo_max_days_ago}
                      onChange={(e) => setFormData({ ...formData, demo_max_days_ago: e.target.value })}
                      placeholder="e.g., 30"
                      min="1"
                      className="admin-input"
                    />
                    <small>Demo must have ended within this many days. Leave empty for any demo.</small>
                  </div>
                )}
              </div>
              <div className="admin-modal-footer">
                <button type="button" onClick={() => setShowEditModal(false)} className="admin-btn admin-btn--secondary">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="admin-btn admin-btn--primary">
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Redemptions Modal */}
      {showRedemptionsModal && selectedPromo && createPortal(
        <div className="admin-modal-overlay" onClick={() => setShowRedemptionsModal(false)}>
          <div className="admin-modal admin-modal--large admin-modal--glass" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Redemptions: {selectedPromo.code}</h3>
              <button onClick={() => setShowRedemptionsModal(false)} className="admin-modal-close">&times;</button>
            </div>
            <div className="admin-modal-body">
              {redemptionsLoading ? (
                <div className="admin-loading">
                  <div className="admin-spinner"></div>
                  Loading redemptions...
                </div>
              ) : redemptions.length === 0 ? (
                <p className="admin-empty-message">No redemptions yet</p>
              ) : (
                <table className="admin-table admin-table--compact">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Discount Amount</th>
                      <th>Redeemed At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {redemptions.map((r) => {
                      const statusInfo = REDEMPTION_STATUS[r.status] || REDEMPTION_STATUS.confirmed;
                      const isInactive = r.status === 'revoked' || r.status === 'reset';
                      return (
                        <tr key={r.id} style={isInactive ? { opacity: 0.6 } : undefined}>
                          <td>{r.user_name || '-'}</td>
                          <td>{r.user_email}</td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: statusInfo.color,
                              background: statusInfo.bg,
                              textDecoration: isInactive ? 'line-through' : 'none'
                            }}>
                              {statusInfo.label}
                            </span>
                            {isInactive && (
                              <span style={{ marginLeft: '6px', fontSize: '11px', color: '#999' }}>Slot freed</span>
                            )}
                          </td>
                          <td style={isInactive ? { textDecoration: 'line-through' } : undefined}>
                            &euro;{r.discount_amount.toFixed(2)}
                          </td>
                          <td>{formatDateTime(r.redeemed_at)}</td>
                          <td>
                            {!isInactive && (
                              confirmResetId === r.id ? (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <button
                                    className="admin-btn admin-btn--danger"
                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                    disabled={resettingId === r.id}
                                    onClick={() => handleResetRedemption(r.id)}
                                  >
                                    {resettingId === r.id ? 'Resetting...' : 'Confirm'}
                                  </button>
                                  <button
                                    className="admin-btn admin-btn--secondary"
                                    style={{ fontSize: '11px', padding: '2px 8px' }}
                                    onClick={() => setConfirmResetId(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="admin-btn admin-btn--secondary"
                                  style={{ fontSize: '11px', padding: '2px 8px' }}
                                  onClick={() => setConfirmResetId(r.id)}
                                  title="Reset this redemption so the user can use the code again"
                                >
                                  Reset
                                </button>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="admin-modal-footer">
              <button onClick={() => setShowRedemptionsModal(false)} className="admin-btn admin-btn--secondary">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* History Modal */}
      {showHistoryModal && selectedPromo && createPortal(
        <div className="admin-modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="admin-modal admin-modal--large admin-modal--glass" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>History: {selectedPromo.code}</h3>
              <button onClick={() => setShowHistoryModal(false)} className="admin-modal-close">&times;</button>
            </div>
            <div className="admin-modal-body">
              {historyLoading ? (
                <div className="admin-loading">
                  <div className="admin-spinner"></div>
                  Loading history...
                </div>
              ) : history.length === 0 ? (
                <p className="admin-empty-message">No history available</p>
              ) : (
                <div className="admin-history-list">
                  {history.map((entry) => {
                    const actionInfo = ACTION_LABELS[entry.action] || { label: entry.action, color: '#6b7280' };
                    return (
                      <div key={entry.id} className="admin-history-item">
                        <div className="admin-history-header">
                          <span
                            className="admin-history-action"
                            style={{ backgroundColor: actionInfo.color }}
                          >
                            {actionInfo.label}
                          </span>
                          <span className="admin-history-date">
                            {formatDateTime(entry.created_at)}
                          </span>
                        </div>
                        <div className="admin-history-actor">
                          {entry.actor_email || 'System'}
                          {entry.ip_address && <span className="admin-text--muted"> ({entry.ip_address})</span>}
                        </div>
                        {entry.details && (
                          <div className="admin-history-details">
                            {formatHistoryDetails(entry.details)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button onClick={() => setShowHistoryModal(false)} className="admin-btn admin-btn--secondary">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
